import { Link } from "react-router-dom";
import LegalShell, { Section, MailLink } from "@/components/LegalShell";

const li = "list-disc pl-5 space-y-1";

export default function Privacy() {
  return (
    <LegalShell kicker="Legal" title="Privacy Policy" updated="October 19, 2026" testId="privacy-page">
      <p>
        Astra ("Astra", "we", "us") is a personalised astrology app operated by Astralogico, available at astralogico.com
        and as an iPhone app. This policy explains what we collect, why, who we share it with, and the choices you have.
        We keep it short and plain on purpose.
      </p>

      <Section title="1. Information we collect">
        <p><strong className="text-starlight">Information you give us</strong></p>
        <ul className={li}>
          <li>Account details: your name and email address, and a securely hashed password if you sign up with email.</li>
          <li>Sign in with Apple: Apple shares a unique identifier and either your email or a private relay email. We never receive your Apple password.</li>
          <li>Birth details: date of birth, and optionally your time and place of birth (stored as a city with its coordinates and time zone) to calculate your chart.</li>
          <li>Questions you ask Astra Interactive, and readings or answers you choose to save to your archive.</li>
          <li>Preferences such as reminder emails, push notifications and reminder time.</li>
        </ul>
        <p><strong className="text-starlight">Information collected automatically</strong></p>
        <ul className={li}>
          <li>Push notification token, if you allow notifications on iPhone, so we can deliver reminders to your device.</li>
          <li>IP address, used to limit free guest questions, prevent abuse and estimate your region for local pricing. We do not store precise location.</li>
          <li>Basic usage statistics measured by Astra itself: pages viewed, time spent, the site that referred you, device type and country (from your region setting). These use a random identifier stored on your device and are used only to improve Astra.</li>
          <li>On the website only: basic usage analytics (Google Analytics) and advertising cookies (Google AdSense) for free visitors.</li>
        </ul>
        <p>We do not collect contacts, photos, health data or precise GPS location.</p>
      </Section>

      <Section title="2. How we use your information">
        <ul className={li}>
          <li>To create and secure your account and keep you signed in.</li>
          <li>To calculate your birth chart and generate your daily, weekly and personal readings.</li>
          <li>To answer the questions you ask Astra Interactive.</li>
          <li>To send reminders, retrograde alerts and account emails you have opted into. You can turn these off at any time.</li>
          <li>To process purchases, apply referral rewards and provide customer support.</li>
          <li>To keep Astra safe, prevent fraud and improve the service.</li>
        </ul>
        <p>We never sell your personal data and we do not use it for cross-app tracking. The iPhone app shows no third-party ads.</p>
      </Section>

      <Section title="3. AI-generated readings">
        <p>
          Readings and chat answers are written by an AI language model (Anthropic Claude), using your first name, sign
          placements, birth details and your question. This information is sent to our AI provider only to generate your
          response and is not used by them to train their models. Readings are for entertainment and reflection, and are not
          medical, legal, financial or psychological advice.
        </p>
      </Section>

      <Section title="4. Who we share data with">
        <p>We share only what each provider needs to run Astra:</p>
        <ul className={li}>
          <li><strong className="text-starlight">Hosting & AI:</strong> Emergent (application hosting and database) and Anthropic (AI readings).</li>
          <li><strong className="text-starlight">Payments:</strong> Stripe processes web purchases. We never see or store your full card details.</li>
          <li><strong className="text-starlight">Email:</strong> Resend delivers our emails.</li>
          <li><strong className="text-starlight">Apple:</strong> Sign in with Apple and Apple Push Notification service.</li>
          <li><strong className="text-starlight">Google (website only):</strong> Analytics and AdSense. You can opt out of personalised ads at adssettings.google.com.</li>
        </ul>
        <p>We may also disclose information if required by law, or to protect the rights and safety of our users.</p>
      </Section>

      <Section title="5. Sharing you control">
        <p>
          If you share a reading, chat answer or compatibility link, anyone with that link can view the shared content. Shared
          pages show the reading text and sign, not your email or exact birth details. Referral links contain a short code,
          not your personal information.
        </p>
      </Section>

      <Section title="6. How long we keep data">
        <p>
          We keep your account data while your account is active. When you delete your account, your profile, birth details,
          readings, chat history and push tokens are removed immediately. Payment records may be kept by Stripe as required by
          tax and accounting law.
        </p>
      </Section>

      <Section title="7. Your rights & choices">
        <ul className={li}>
          <li>Access or correct your details at any time in Sanctuary (your profile).</li>
          <li>Turn email reminders and push notifications on or off in Sanctuary, or in iPhone Settings.</li>
          <li>Delete your account and all associated data in Sanctuary → Delete account. This is immediate and permanent.</li>
          <li>Ask for a copy of your data, or make any other privacy request, by emailing <MailLink subject="Privacy request" />.</li>
        </ul>
        <p>
          Depending on where you live (for example the UK, EU or California), you may have additional rights to access,
          port, restrict or object to processing, and to complain to your local data protection authority. We do not sell or
          "share" personal information as defined under California law.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          Data is encrypted in transit (HTTPS). Passwords are hashed and never stored in plain text, and access to our
          systems is restricted. No system is perfectly secure, but we work hard to protect your information.
        </p>
      </Section>

      <Section title="9. Children">
        <p>Astra is not directed to children under 13 and we do not knowingly collect their data. If you believe a child has given us data, contact us and we will delete it.</p>
      </Section>

      <Section title="10. International users">
        <p>Your information may be processed in countries other than your own, including the United States. Where required, we rely on appropriate safeguards for these transfers.</p>
      </Section>

      <Section title="11. Changes">
        <p>We may update this policy from time to time. We will change the date above and, for significant changes, let you know in the app or by email.</p>
      </Section>

      <Section title="12. Contact">
        <p>Questions about privacy? Email <MailLink subject="Privacy question" />. For help using Astra, visit our <Link to="/support" data-testid="privacy-support-link" className="gold-text underline underline-offset-4">Support page</Link>.</p>
      </Section>
    </LegalShell>
  );
}
